// rateLimit.js — sequential-upload pacing + backoff (plan Step 7)
//
// Publish does N sequential uploadBlob calls plus one createRecord. PDS
// rate limits are authoritative via response headers: Retry-After
// (seconds or HTTP date) and RateLimit-Reset (epoch seconds). On 429/5xx
// we wait the header-specified delay, falling back to exponential backoff.

export const DEFAULT_BASE_DELAY_MS = 1000;
export const DEFAULT_MAX_DELAY_MS = 30000;
export const DEFAULT_MAX_RETRIES = 5;
export const DEFAULT_INTER_REQUEST_DELAY_MS = 250;

export const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

/**
 * Extract a retry delay in ms from an XRPC error's headers.
 * Sources, in priority order:
 *   Retry-After: <seconds> | <HTTP-date>
 *   RateLimit-Reset: <epoch seconds>
 * Returns null when no usable header is present.
 */
export function retryDelayFromError(err, now = Date.now()) {
  if (!err) return null;
  const headers = err.headers || err.response?.headers;

  if (!headers) return null;

  const get = (name) => {
    if (headers.get !== undefined) return headers.get(name);
    const lower = name.toLowerCase();

    return headers[lower] ?? headers[name];
  };

  const retryAfter = get('retry-after');

  if (retryAfter) {
    const seconds = Number(retryAfter);

    if (Number.isFinite(seconds) && seconds > 0) {
      return seconds * 1000;
    }

    // HTTP-date form.
    const date = Date.parse(retryAfter);

    if (Number.isFinite(date)) {
      return Math.max(0, date - now);
    }
  }

  const rateLimitReset = get('ratelimit-reset');

  if (rateLimitReset) {
    const epochSeconds = Number(rateLimitReset);

    if (Number.isFinite(epochSeconds) && epochSeconds > 0) {
      return Math.max(0, epochSeconds * 1000 - now);
    }
  }

  return null;
}

/**
 * True when the error is retryable: 429 (rate limit) or 5xx (server).
 * Client errors (4xx except 429) are NOT retried — retrying a bad request
 * just burns rate budget.
 */
export function isRetryable(err) {
  const status = err?.status ?? err?.statusCode;

  return status === 429 || (status >= 500 && status < 600);
}

/**
 * Run fn with exponential backoff on retryable errors.
 *
 * @param {() => Promise<any>} fn
 * @param {Object} [opts]
 * @param {number} [opts.maxRetries=5]
 * @param {number} [opts.baseDelayMs=1000]
 * @param {number} [opts.maxDelayMs=30000]
 * @param {(err:any, attempt:number) => void} [opts.onRetry]
 */
export async function withBackoff(fn, opts = {}) {
  const {
    maxRetries = DEFAULT_MAX_RETRIES,
    baseDelayMs = DEFAULT_BASE_DELAY_MS,
    maxDelayMs = DEFAULT_MAX_DELAY_MS,
    onRetry,
  } = opts;

  let attempt = 0;

  // eslint-disable-next-line no-constant-condition
  while (true) {
    try {
      return await fn();
    } catch (err) {
      if (!isRetryable(err) || attempt >= maxRetries) throw err;
      const headerDelay = retryDelayFromError(err);
      const backoff = Math.min(maxDelayMs, baseDelayMs * 2 ** attempt);
      const delay = headerDelay ?? backoff;
      onRetry?.(err, attempt, delay);
      await sleep(delay);
      attempt += 1;
    }
  }
}

/**
 * Sequential map with a small inter-request delay between items, each
 * wrapped in withBackoff. Used for the N uploadBlob calls.
 */
export async function sequentialWithPacing(items, fn, opts = {}) {
  const { interRequestDelayMs = DEFAULT_INTER_REQUEST_DELAY_MS } = opts;
  const results = [];

  for (let i = 0; i < items.length; i++) {
    const result = await withBackoff(() => fn(items[i], i), opts);
    results.push(result);

    if (i < items.length - 1 && interRequestDelayMs > 0) {
      await sleep(interRequestDelayMs);
    }
  }

  return results;
}